"use strict";
/**
 * App-wide identity and focus-context events.
 *
 * Registered as Angular factory `appEventsService` (see main.ts).
 * Controllers/services inject it — do not $broadcast raw strings from AudRoamCtrl.
 *
 * Events (also on APP_EVENTS):
 *   auth:userChanged        — login / logout / person switch / token refresh / session end
 *   focus:contextChanged    — account/realm/scan/Focus T·S ids moved
 *   auth:tokenRefreshed     — JWT rotated (authInterceptor)
 *   auth:serverUnreachable  — refresh transport failed; session kept
 *   auth:sessionEnded       — JWT cleared; re-login required
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.appEventsServiceFactory = exports.AppEventsServiceImpl = exports.focusContextChanged = exports.buildUserChangedPayload = exports.buildFocusContextPayload = exports.isAuthenticatedPerson = exports.personPkOf = exports.APP_EVENTS = void 0;
exports.APP_EVENTS = {
    USER_CHANGED: 'auth:userChanged',
    TOKEN_REFRESHED: 'auth:tokenRefreshed',
    SERVER_UNREACHABLE: 'auth:serverUnreachable',
    SESSION_ENDED: 'auth:sessionEnded',
    FOCUS_CONTEXT_CHANGED: 'focus:contextChanged',
};
// --- pure helpers (unit-tested without Angular) ---
function personPkOf(p) {
    if (!p)
        return null;
    if (p.dbPK != null && p.dbPK !== '')
        return p.dbPK;
    if (p.personId != null && p.personId !== '')
        return p.personId;
    return null;
}
exports.personPkOf = personPkOf;
function isAuthenticatedPerson(p) {
    if (!p)
        return false;
    return !!(p.authenticated || p.loginName || personPkOf(p) != null);
}
exports.isAuthenticatedPerson = isAuthenticatedPerson;
function buildFocusContextPayload(reason, host) {
    var _a, _b;
    var person = host.person;
    var openMeta = host.openMeta;
    var sessionMeta = host.sessionMeta;
    return {
        reason: reason,
        personId: personPkOf(person),
        account: (person === null || person === void 0 ? void 0 : person.CurrentAccount) || null,
        realm: (person === null || person === void 0 ? void 0 : person.CurrentRealm) || null,
        scanAccount: (person === null || person === void 0 ? void 0 : person.ScanAccount) || null,
        scanRealm: (person === null || person === void 0 ? void 0 : person.ScanRealm) || null,
        scanAsset: (person === null || person === void 0 ? void 0 : person.ScanAsset) || null,
        focusTId: (_a = openMeta === null || openMeta === void 0 ? void 0 : openMeta.openId) !== null && _a !== void 0 ? _a : null,
        focusSId: (_b = sessionMeta === null || sessionMeta === void 0 ? void 0 : sessionMeta.openId) !== null && _b !== void 0 ? _b : null,
        openMeta: openMeta,
        sessionMeta: sessionMeta,
        idleMeta: host.idleMeta,
    };
}
exports.buildFocusContextPayload = buildFocusContextPayload;
function buildUserChangedPayload(reason, person, previousPersonId) {
    return {
        reason: reason,
        personId: personPkOf(person),
        previousPersonId: previousPersonId !== undefined ? previousPersonId : null,
        person: person || null,
        authenticated: isAuthenticatedPerson(person),
    };
}
exports.buildUserChangedPayload = buildUserChangedPayload;
function focusContextChanged(prev, next) {
    var _a, _b, _c, _d, _f, _g, _h, _j;
    return (prev.account !== next.account ||
        prev.realm !== next.realm ||
        String((_a = prev.focusTId) !== null && _a !== void 0 ? _a : '') !== String((_b = next.focusTId) !== null && _b !== void 0 ? _b : '') ||
        String((_c = next.focusSId) !== null && _c !== void 0 ? _c : '') !== String((_d = prev.focusSId) !== null && _d !== void 0 ? _d : '') ||
        String((_f = prev.scanAsset) !== null && _f !== void 0 ? _f : '') !== String((_g = next.scanAsset) !== null && _g !== void 0 ? _g : '') ||
        String((_h = prev.personId) !== null && _h !== void 0 ? _h : '') !== String((_j = next.personId) !== null && _j !== void 0 ? _j : ''));
}
exports.focusContextChanged = focusContextChanged;
var AppEventsServiceImpl = /** @class */ (function () {
    function AppEventsServiceImpl($rootScope) {
        this.$rootScope = $rootScope;
        this.events = exports.APP_EVENTS;
        this.personPkOf = personPkOf;
        this.isAuthenticatedPerson = isAuthenticatedPerson;
    }
    AppEventsServiceImpl.prototype.buildFocusContext = function (reason, host) {
        return buildFocusContextPayload(reason, host);
    };
    AppEventsServiceImpl.prototype.emitUserChanged = function (reason, person, previousPersonId) {
        var payload = buildUserChangedPayload(reason, person, previousPersonId);
        try {
            this.$rootScope.$broadcast(exports.APP_EVENTS.USER_CHANGED, payload);
        }
        catch (_a) {
            /* ignore */
        }
        return payload;
    };
    AppEventsServiceImpl.prototype.emitFocusContext = function (reason, host) {
        var payload = buildFocusContextPayload(reason, host);
        try {
            this.$rootScope.$broadcast(exports.APP_EVENTS.FOCUS_CONTEXT_CHANGED, payload);
        }
        catch (_a) {
            /* ignore */
        }
        return payload;
    };
    AppEventsServiceImpl.prototype.emitFocusContextIfChanged = function (reason, host, previous) {
        var next = buildFocusContextPayload(reason, host);
        if (previous && !focusContextChanged(previous, next)) {
            return null;
        }
        try {
            this.$rootScope.$broadcast(exports.APP_EVENTS.FOCUS_CONTEXT_CHANGED, next);
        }
        catch (_a) {
            /* ignore */
        }
        return next;
    };
    AppEventsServiceImpl.prototype.onUserChanged = function (handler) {
        return this.$rootScope.$on(exports.APP_EVENTS.USER_CHANGED, function (_e, payload) {
            handler(payload);
        });
    };
    AppEventsServiceImpl.prototype.onFocusContextChanged = function (handler) {
        return this.$rootScope.$on(exports.APP_EVENTS.FOCUS_CONTEXT_CHANGED, function (_e, payload) {
            handler(payload);
        });
    };
    AppEventsServiceImpl.prototype.onTokenRefreshed = function (handler) {
        return this.$rootScope.$on(exports.APP_EVENTS.TOKEN_REFRESHED, function (_e, data) { return handler(data); });
    };
    AppEventsServiceImpl.prototype.onSessionEnded = function (handler) {
        return this.$rootScope.$on(exports.APP_EVENTS.SESSION_ENDED, function (_e, data) { return handler(data); });
    };
    AppEventsServiceImpl.prototype.onServerUnreachable = function (handler) {
        return this.$rootScope.$on(exports.APP_EVENTS.SERVER_UNREACHABLE, function (_e, data) { return handler(data); });
    };
    return AppEventsServiceImpl;
}());
exports.AppEventsServiceImpl = AppEventsServiceImpl;
exports.appEventsServiceFactory = [
    '$rootScope',
    function ($rootScope) {
        return new AppEventsServiceImpl($rootScope);
    },
];
